import {LoginUser} from '@/interfaces/loginUser.interface';
import Axios from 'axios';
import {Types} from 'mongoose';
import {API, ApiRequestOptions} from './api.class';
import {SendDefaultsPreferences, UserPreferences} from '@/interfaces/clientOnly/userPreferences.interface';
import {backendFeatures, isUnknownRoute, silentRequest} from '@/helpers/featureProbe.helper';
import {FindLoginUserRequestDto} from '@/classes/dto/user-login-handler/request/FindLoginUser.request.dto';
import {ActivateLoginUserRequestDto} from '@/classes/dto/user-login-handler/request/ActivateLoginUser.request.dto';
import {FindByIdOptions} from '@/interfaces/findByIdOptions';
import {CreateLoginUserRequestType} from '@/classes/dto/user-login-handler/request/CreateLoginUser.request.dto';
import {UpdateLoginUserRequestDto} from '@/classes/dto/user-login-handler/request/UpdateLoginUser.request.dto';
import {
    GenerateApplicationTokenRequestDto,
} from '@/classes/dto/user-login-handler/request/generateApplicationToken.request.dto';

/**
 * This class is used for communicating with user-login-handler
 * @class UserLoginHandlerApi
 * @author mklaczinski
 */
export class UserLoginHandlerApi extends API {
    static service = 'user-login-handler';

    static async activate(obj: ActivateLoginUserRequestDto): Promise<boolean | void> {
        return Axios.post(`${process.env.VUE_APP_API_PATH}/${this.service}/activate`, obj);
    }

    static create(data: CreateLoginUserRequestType): Promise<void> {
        return this.post(``, data);
    }

    static async findByMeta(id: Types.ObjectId): Promise<LoginUser> {
        return this.get(`meta/${id}`, '');
    }

    /** requestOptions.handledStatuses: e.g. 403 for optional lookups the caller handles itself */
    static async findById(id: Types.ObjectId, options?: FindByIdOptions, requestOptions?: ApiRequestOptions): Promise<LoginUser> {
        return this.get(`${id}`, options, undefined, requestOptions);
    }

    static async find(data: FindLoginUserRequestDto, requestOptions?: ApiRequestOptions): Promise<{ loginUsers: LoginUser[]; maxPage: number }> {
        return this.get(``, data, undefined, requestOptions);
    }

    static async update(id: Types.ObjectId, update: UpdateLoginUserRequestDto): Promise<LoginUser> {
        return this.put(`${id}`, update);
    }

    static async deactivate(id: Types.ObjectId): Promise<LoginUser> {
        return this.put(`deactivate/${id}`, {});
    }

    static async reactivate(id: Types.ObjectId): Promise<LoginUser> {
        return this.put(`reactivate/${id}`, {});
    }

    static async getWidgets(): Promise<string[]> {
        return this.get('widgets');
    }

    static async setWidgets(widgets: string[]): Promise<void> {
        await this.post('widgets', widgets);
    }

    static async generateToken(data: GenerateApplicationTokenRequestDto): Promise<string> {
        return this.post('generateToken', data);
    }

    /**
     * Notify v2: own notification settings and UI preferences. Silent: resolves null on an old backend
     * (no dialog) and remembers that the endpoint is missing.
     */
    static async getMyPreferences(): Promise<UserPreferences | null> {
        const result = await silentRequest<UserPreferences>('get', this.service, 'me/preferences');
        const valid = result.ok && !!result.data && typeof result.data === 'object'
            && ('notificationSettings' in result.data || 'uiPreferences' in result.data);
        if (valid && result.ok) {
            backendFeatures.preferences = true;
            return result.data;
        }
        if (backendFeatures.preferences === undefined) {
            backendFeatures.preferences = false;
        }
        return null;
    }

    /** Partial merge of the own preferences. Silent: false when the backend has no endpoint or the call failed. */
    static async updateMyPreferences(update: UserPreferences): Promise<boolean> {
        if (backendFeatures.preferences === false) {
            return false;
        }
        const result = await silentRequest('put', this.service, 'me/preferences', update);
        return result.ok;
    }

    /**
     * Stores "Meine Versand-Standards" at the user (whole object, null removes). Silent.
     * `unsupported`: the backend does not know the field yet (old user-login-handler drops it, the answer has no
     * sendDefaults) or has no preferences endpoint – the defaults then stay in this browser.
     */
    static async saveMySendDefaults(sendDefaults: SendDefaultsPreferences | null): Promise<'saved' | 'unsupported' | 'failed'> {
        if (backendFeatures.preferences === false) {
            return 'unsupported';
        }
        const result = await silentRequest<UserPreferences>('put', this.service, 'me/preferences', {sendDefaults});
        if (!result.ok) {
            // unknown route (no preferences) or rejected payload (400/422): no retry loop, the defaults stay local
            const rejected = result.status === 400 || result.status === 422;
            return isUnknownRoute(result) || rejected ? 'unsupported' : 'failed';
        }
        const stored = result.data && typeof result.data === 'object' ? (result.data as UserPreferences).sendDefaults : undefined;
        return sendDefaults === null || stored ? 'saved' : 'unsupported';
    }
}
